import { useState } from 'react';
import styles from './Interactive.module.css';

interface ClozeBlank {
  id: string;
  acceptedAnswers: string[];
}

interface ClozeConfig {
  textWithBlanks: string;
  blanks: ClozeBlank[];
}

interface ClozeProps {
  config: ClozeConfig;
  onSubmit: (answer: string) => void;
  disabled: boolean;
  showResult: boolean;
  correctAnswer?: string;
}

export function Cloze({ config, onSubmit, disabled, showResult, correctAnswer }: ClozeProps) {
  const [values, setValues] = useState<Record<string, string>>({});

  // Parse correct answers for result display
  const correctAnswers: Record<string, boolean> = {};
  if (showResult && correctAnswer) {
    try {
      JSON.parse(correctAnswer);  // Validate JSON format
      config.blanks.forEach((blank) => {
        const userAnswer = (values[blank.id] || '').toLowerCase().trim();
        const accepted = blank.acceptedAnswers.map((a) => a.toLowerCase().trim());
        correctAnswers[blank.id] = accepted.includes(userAnswer);
      });
    } catch {
      // Ignore parse errors
    }
  }

  const handleChange = (blankId: string, value: string) => {
    if (disabled) return;
    setValues((prev) => ({ ...prev, [blankId]: value }));
  };

  const handleSubmit = () => {
    if (disabled) return;
    onSubmit(JSON.stringify(values));
  };

  const getInputClass = (blankId: string) => {
    let className = styles.blankInput;
    if (showResult) {
      className += correctAnswers[blankId] ? ` ${styles.correct}` : ` ${styles.incorrect}`;
    }
    return className;
  };

  // Parse text and replace {{1}}, {{2}} etc. with inputs
  const renderText = () => {
    const text = config.textWithBlanks;
    const parts = text.split(/(\{\{\d+\}\})/g);

    return parts.map((part, idx) => {
      const match = part.match(/\{\{(\d+)\}\}/);
      if (match) {
        const blankId = match[1];
        const blankIndex = parseInt(blankId) - 1;
        const blank = config.blanks[blankIndex];

        if (!blank) return <span key={idx}>{part}</span>;

        return (
          <span key={idx} className={styles.clozeBlank}>
            <span className={styles.clozeBlankNumber}>{blankId}.</span>
            <input
              type="text"
              className={getInputClass(blank.id)}
              value={values[blank.id] || ''}
              onChange={(e) => handleChange(blank.id, e.target.value)}
              placeholder={`blank ${blankId}`}
              disabled={disabled}
            />
          </span>
        );
      }
      return <span key={idx}>{part}</span>;
    });
  };

  const allFilled = config.blanks.every((blank) => values[blank.id]?.trim());

  return (
    <div className={styles.clozeContainer}>
      <div className={styles.clozeText}>{renderText()}</div>

      {!showResult && (
        <div className={styles.inputContainer}>
          <button
            className={styles.submitButton}
            onClick={handleSubmit}
            disabled={disabled || !allFilled}
          >
            Submit Answers
          </button>
        </div>
      )}
    </div>
  );
}
